import React from 'react';
import { sensorUnits } from 'commons/src/constants';
import { Rating, SensorTypes, ThresholdType } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import { CustomOptions, ThresholdOption } from 'commons/src/models/customThresholdModels';
import SensorThresholdMgmt from './SensorThresholdMgmt';
import styles from './ThresholdMgmt.module.scss';

type AccountThresholds = {
    customOptions?: CustomOptions;
    ranges: ThresholdRange[];
    unit: keyof typeof sensorUnits;
    thresholdOptions: ThresholdOption[];
};

type MockDataType = {
    locationsWithThresholds: [
        {
            id: string;
            name: string;
            sensors: SensorTypes[];
        }
    ];
    thresholds: {
        [sensorType: string]: AccountThresholds;
    };
};

const mockData: MockDataType = {
    locationsWithThresholds: [
        {
            id: 'd165d34b-2189-4aa5-9f72-5e687369f7e5',
            name: 'Airthings office',
            sensors: [SensorTypes.temp, SensorTypes.voc],
        },
    ],
    thresholds: {
        temp: {
            ranges: [
                {
                    to: -5,
                    rating: Rating.POOR,
                },
                {
                    from: -5,
                    to: 14,
                    rating: Rating.GOOD,
                },
                {
                    from: 14,
                    rating: Rating.POOR,
                },
            ],
            unit: 'c',
            customOptions: {
                defaultHigh: 22,
                defaultLow: 19,
                minSelectableValue: -30,
                maxSelectableValue: 40,
                thresholdDelta: 1.0,
                defaultHighAlert: 25,
                defaultLowAlert: 18,
                defaultThresholdOptions: [
                    {
                        type: ThresholdType.LOW_ALERT,
                        value: 18,
                    },
                    {
                        type: ThresholdType.HIGH_ALERT,
                        value: 25,
                    },
                ],
            },
            thresholdOptions: [
                {
                    type: ThresholdType.LOW_ALERT,
                    value: -5.0,
                },
                {
                    type: ThresholdType.HIGH_ALERT,
                    value: 14.0,
                },
            ],
        },
        humidity: {
            ranges: [
                {
                    to: 25,
                    rating: Rating.POOR,
                },
                {
                    from: 25,
                    to: 30,
                    rating: Rating.FAIR,
                },
                {
                    from: 30,
                    to: 60,
                    rating: Rating.GOOD,
                },
                {
                    from: 60,
                    to: 70,
                    rating: Rating.FAIR,
                },
                {
                    from: 70,
                    rating: Rating.POOR,
                },
            ],
            unit: 'pct',
            customOptions: {
                defaultHigh: 60,
                defaultLow: 30,
                minSelectableValue: 0,
                maxSelectableValue: 100,
                thresholdDelta: 2.0,
                defaultHighAlert: 70,
                defaultLowAlert: 25,
                defaultHighWarn: 60,
                defaultLowWarn: 30,
                defaultThresholdOptions: [
                    {
                        type: ThresholdType.LOW_WARN,
                        value: 30,
                    },
                    {
                        type: ThresholdType.HIGH_WARN,
                        value: 60,
                    },
                    {
                        type: ThresholdType.LOW_ALERT,
                        value: 25,
                    },
                    {
                        type: ThresholdType.HIGH_ALERT,
                        value: 70,
                    },
                ],
            },
            thresholdOptions: [
                {
                    type: ThresholdType.LOW_WARN,
                    value: 30,
                },
                {
                    type: ThresholdType.HIGH_WARN,
                    value: 60,
                },
                {
                    type: ThresholdType.LOW_ALERT,
                    value: 25,
                },
                {
                    type: ThresholdType.HIGH_ALERT,
                    value: 70,
                },
            ],
        },
    },
};

const ThresholdMgmt = (): React.ReactElement => {
    const thresholdData = mockData.thresholds;
    const onSave = (): void => {
        // console.log('save');
    };
    return (
        <div className={styles.list}>
            {Object.keys(thresholdData).map(sensorType => (
                <SensorThresholdMgmt
                    key={sensorType}
                    sensorType={sensorType as SensorTypes}
                    thresholds={thresholdData[sensorType]}
                    onSave={onSave}
                />
            ))}
        </div>
    );
};

export default ThresholdMgmt;
